import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wand2, Brain, Globe, Search, ShoppingBag, MessageCircle, Share2, ChevronDown, ChevronUp, RefreshCw, CheckCircle2, Clock, Edit2, Download, ExternalLink, Store, Film } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { TopBar } from '../components/TopBar';
import { BottomNav } from '../components/BottomNav';


const ContentSection = ({ title, icon: Icon, status, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 text-left">
        <Icon size={16} className="text-[#128C7E] flex-shrink-0" />
        <span className="text-sm font-bold text-slate-700 flex-1">{title}</span>
        {status === 'ready' && <CheckCircle2 size={14} className="text-emerald-500" />}
        {status === 'pending' && <Clock size={14} className="text-amber-500" />}
        {open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>
      {open && <div className="p-4 space-y-3 bg-white">{children}</div>}
    </div>
  );
};

export const ContentStudioScreen = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null); // tracks which content is generating

  useEffect(() => { fetchProduct(); }, [productId]);

  const fetchProduct = async () => {
    try {
      const res = await api.get(`/products/${productId}`);
      setProduct(res.data.product);
    } catch (e) { toast.error('Product not found'); navigate('/my-products'); }
    setLoading(false);
  };

  const runAnalysis = async () => {
    setGenerating('analysis');
    try {
      await api.post(`/products/${productId}/analyze`, {}, { timeout: 120000 });
      toast.success('Product analyzed by AI');
      fetchProduct();
    } catch (e) { toast.error('Analysis failed: ' + (e.response?.data?.detail || e.message)); }
    setGenerating(null);
  };

  const generateContent = async (contentType) => {
    setGenerating(contentType);
    try {
      await api.post(`/content/generate`, {
        product_id: productId, content_type: contentType
      }, { timeout: 300000 });
      toast.success(contentType === 'marketing_kit' ? 'Complete Marketing Kit generated!' : `${contentType} generated!`);
      fetchProduct();
    } catch (e) { toast.error('Generation failed: ' + (e.response?.data?.detail || e.message)); }
    setGenerating(null);
  };

  const handleShare = () => {
    if (!product) return;
    const title = product.ai_title || product.name || 'Check out this product';
    const desc = product.ai_description ? product.ai_description.substring(0, 200) + '...' : '';
    const price = product.price ? `Price: ₹${product.price}` : '';
    const text = `${title}\n\n${desc}\n${price}\n\nPowered by SellGen AI`;
    const encoded = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
  };

  const handleExport = (platform) => {
    navigate('/export-success', {
      state: { platform, productId, fileName: `${(product?.ai_title || 'product').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}_${platform}.csv` }
    });
  };

  if (loading) return <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center"><p>Loading...</p></div>;
  if (!product) return null;

  const analysis = product.ai_analysis;
  const seo = product.seo_data;
  const website = product.website_content;
  const mpListings = product.marketplace_listings || [];
  const socialContent = product.social_content || [];
  const score = product.completion_score || 0;

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar title="Content Studio" showBack={true} action={
          <button onClick={handleShare} className="text-white" data-testid="share-btn"><Share2 size={20} /></button>
        } />

        <div className="p-4 space-y-4">
          {/* Product Header */}
          <div className="flex gap-3 items-start">
            <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
              {product.images?.[0] ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${product.images[0].path}`} alt="" className="w-full h-full object-cover" />
              ) : <div className="w-full h-full bg-gray-200" />}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold text-slate-800 line-clamp-2" data-testid="product-title">{product.ai_title || product.name || 'Untitled Product'}</h2>
              {product.price && <p className="text-lg font-bold text-[#128C7E]">₹{product.price}</p>}
            </div>
          </div>

          {/* Marketing Score */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Marketing Score</p>
                <p className="text-3xl font-bold text-purple-800">{score}<span className="text-sm font-normal">/100</span></p>
              </div>
              <div className="w-16 h-16 relative">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                  <path className="text-purple-100" stroke="currentColor" strokeWidth="3" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className="text-purple-600" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray={`${score}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
              </div>
            </div>
          </div>

          {/* Generate Everything Button */}
          <button onClick={() => generateContent('marketing_kit')} disabled={generating}
            className="w-full h-14 bg-gradient-to-r from-[#128C7E] to-[#25D366] text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50 shadow-lg"
            data-testid="generate-kit-btn"
          >
            {generating === 'marketing_kit' ? (
              <><RefreshCw size={18} className="animate-spin" /> Generating Complete Kit...</>
            ) : (
              <><Wand2 size={18} /> Generate Complete Marketing Kit</>
            )}
          </button>

          {/* AI Product Intelligence */}
          <ContentSection title="Product Intelligence" icon={Brain} status={analysis ? 'ready' : 'pending'} defaultOpen={!analysis}>
            {analysis ? (
              <div className="space-y-2">
                {[
                  ['Category', analysis.category],
                  ['Type', analysis.product_type],
                  ['Color', analysis.primary_color],
                  ['Material', analysis.material],
                  ['Style', analysis.style],
                  ['Pattern', analysis.pattern],
                  ['Season', analysis.season],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm">
                    <span className="text-gray-500">{k}</span>
                    <span className="font-medium text-slate-700">{v}</span>
                  </div>
                ))}
                {analysis.key_selling_points?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Key Selling Points</p>
                    <div className="flex flex-wrap gap-1">
                      {analysis.key_selling_points.map((p, i) => <span key={i} className="px-2 py-0.5 bg-green-50 text-[#128C7E] rounded-full text-xs">{p}</span>)}
                    </div>
                  </div>
                )}
                {analysis.target_audience?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Target Audience</p>
                    <div className="flex flex-wrap gap-1">
                      {analysis.target_audience.map((a, i) => <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs">{a}</span>)}
                    </div>
                  </div>
                )}
                <button onClick={runAnalysis} disabled={generating === 'analysis'}
                  className="text-xs text-[#128C7E] font-medium flex items-center gap-1 mt-2"
                >
                  <RefreshCw size={12} className={generating === 'analysis' ? 'animate-spin' : ''} /> Re-analyze
                </button>
              </div>
            ) : (
              <button onClick={runAnalysis} disabled={generating === 'analysis'}
                className="w-full py-3 bg-[#128C7E] text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="analyze-btn"
              >
                {generating === 'analysis' ? <><RefreshCw size={14} className="animate-spin" /> Analyzing...</> : <><Brain size={14} /> Analyze Product with AI</>}
              </button>
            )}
          </ContentSection>

          {/* Product Listing */}
          <ContentSection title="Product Listing" icon={Edit2} status={product.ai_title ? 'ready' : 'pending'} defaultOpen={!!product.ai_title}>
            {product.ai_title ? (
              <div className="space-y-2">
                <div><p className="text-xs text-gray-500">Title</p><p className="text-sm font-semibold">{product.ai_title}</p></div>
                <div><p className="text-xs text-gray-500">Description</p><p className="text-xs text-gray-700 leading-relaxed">{product.ai_description?.substring(0, 300)}...</p></div>
                {product.ai_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">{product.ai_tags.map((t, i) => <span key={i} className="px-2 py-0.5 bg-green-50 text-[#128C7E] rounded-full text-[10px]">{t}</span>)}</div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => navigate(`/product/${productId}`)} className="text-xs text-[#128C7E] font-medium flex items-center gap-1"><Edit2 size={12} /> Edit</button>
                  <button onClick={() => generateContent('listing')} disabled={generating === 'listing'} className="text-xs text-[#128C7E] font-medium flex items-center gap-1">
                    <RefreshCw size={12} className={generating === 'listing' ? 'animate-spin' : ''} /> Regenerate
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => generateContent('listing')} disabled={!!generating}
                className="w-full py-3 bg-gray-100 rounded-lg text-sm font-medium text-gray-600 disabled:opacity-50">Generate Listing</button>
            )}
          </ContentSection>

          {/* SEO */}
          <ContentSection title="SEO Content" icon={Search} status={seo?.seo_title ? 'ready' : 'pending'}>
            {seo?.seo_title ? (
              <div className="space-y-2">
                <div><p className="text-xs text-gray-500">SEO Title</p><p className="text-sm font-semibold">{seo.seo_title}</p></div>
                <div><p className="text-xs text-gray-500">Meta Description</p><p className="text-xs text-gray-700">{seo.meta_description}</p></div>
                {seo.focus_keywords?.length > 0 && (
                  <div><p className="text-xs text-gray-500">Keywords</p><div className="flex flex-wrap gap-1">{seo.focus_keywords.map((k, i) => <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px]">{k}</span>)}</div></div>
                )}
                <button onClick={() => generateContent('seo')} disabled={generating === 'seo'} className="text-xs text-[#128C7E] font-medium flex items-center gap-1">
                  <RefreshCw size={12} className={generating === 'seo' ? 'animate-spin' : ''} /> Regenerate
                </button>
              </div>
            ) : (
              <button onClick={() => generateContent('seo')} disabled={!!generating}
                className="w-full py-3 bg-gray-100 rounded-lg text-sm font-medium text-gray-600 disabled:opacity-50">Generate SEO</button>
            )}
          </ContentSection>

          {/* Website Content */}
          <ContentSection title="Website Description" icon={Globe} status={website?.short_description ? 'ready' : 'pending'}>
            {website?.short_description ? (
              <div className="space-y-2">
                <div><p className="text-xs text-gray-500">Short Description</p><p className="text-xs text-gray-700">{website.short_description}</p></div>
                {website.features?.length > 0 && (
                  <div><p className="text-xs text-gray-500">Features</p><ul className="text-xs text-gray-700 space-y-0.5">{website.features.slice(0, 3).map((f, i) => <li key={i}>• {f}</li>)}</ul></div>
                )}
                <button onClick={() => generateContent('website')} disabled={generating === 'website'} className="text-xs text-[#128C7E] font-medium flex items-center gap-1">
                  <RefreshCw size={12} className={generating === 'website' ? 'animate-spin' : ''} /> Regenerate
                </button>
              </div>
            ) : (
              <button onClick={() => generateContent('website')} disabled={!!generating}
                className="w-full py-3 bg-gray-100 rounded-lg text-sm font-medium text-gray-600 disabled:opacity-50">Generate Website Content</button>
            )}
          </ContentSection>

          {/* Marketplace Listings */}
          {['amazon', 'flipkart', 'etsy'].map(mp => {
            const listing = mpListings.find(l => l.platform === mp);
            return (
              <ContentSection key={mp} title={`${mp.charAt(0).toUpperCase() + mp.slice(1)} Listing`} icon={ShoppingBag} status={listing?.title ? 'ready' : 'pending'}>
                {listing?.title ? (
                  <div className="space-y-2">
                    <div><p className="text-xs text-gray-500">Title</p><p className="text-sm font-semibold">{listing.title}</p></div>
                    <div className="flex gap-2">
                      <button onClick={() => handleExport(mp)} className="text-xs text-[#128C7E] font-medium flex items-center gap-1"><Download size={12} /> Export CSV</button>
                      <button onClick={() => generateContent(`marketplace_${mp}`)} disabled={generating === `marketplace_${mp}`}
                        className="text-xs text-[#128C7E] font-medium flex items-center gap-1">
                        <RefreshCw size={12} className={generating === `marketplace_${mp}` ? 'animate-spin' : ''} /> Regenerate
                      </button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => generateContent(`marketplace_${mp}`)} disabled={!!generating}
                    className="w-full py-3 bg-gray-100 rounded-lg text-sm font-medium text-gray-600 disabled:opacity-50">Generate {mp} Listing</button>
                )}
              </ContentSection>
            );
          })}

          {/* Social Content */}
          {['instagram', 'facebook'].map(platform => {
            const social = socialContent.find(s => s.platform === platform);
            return (
              <ContentSection key={platform} title={`${platform.charAt(0).toUpperCase() + platform.slice(1)} Content`} icon={MessageCircle} status={social?.captions?.length ? 'ready' : 'pending'}>
                {social?.captions?.length ? (
                  <div className="space-y-2">
                    <div><p className="text-xs text-gray-500">Caption</p><p className="text-xs text-gray-700">{social.captions[0]?.substring(0, 150)}...</p></div>
                    {social.hashtags?.length > 0 && (
                      <div className="flex flex-wrap gap-1">{social.hashtags.slice(0, 5).map((h, i) => <span key={i} className="text-[10px] text-blue-600">{h}</span>)}</div>
                    )}
                    <button onClick={() => generateContent(`social_${platform}`)} disabled={generating === `social_${platform}`}
                      className="text-xs text-[#128C7E] font-medium flex items-center gap-1">
                      <RefreshCw size={12} className={generating === `social_${platform}` ? 'animate-spin' : ''} /> Regenerate
                    </button>
                  </div>
                ) : (
                  <button onClick={() => generateContent(`social_${platform}`)} disabled={!!generating}
                    className="w-full py-3 bg-gray-100 rounded-lg text-sm font-medium text-gray-600 disabled:opacity-50">Generate {platform} Content</button>
                )}
              </ContentSection>
            );
          })}

          {/* Visual Commerce Link */}
          <button onClick={() => navigate(`/visual-commerce/${productId}`)}
            className="w-full h-14 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg"
            data-testid="visual-commerce-btn"
          >
            <Wand2 size={18} /> AI Product Photography
          </button>

          {/* AI Video Studio Link */}
          <button onClick={() => navigate(`/video-engine/${productId}`)}
            className="w-full h-14 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg"
            data-testid="video-studio-btn"
          >
            <Film size={18} /> AI Video Studio
          </button>

          {/* Future Phases */}
          {['Meta Ads', 'Google Ads', 'Email Campaign', 'WhatsApp Campaign'].map(feat => (
            <div key={feat} className="border border-dashed border-gray-300 rounded-xl p-4 flex items-center justify-between opacity-60">
              <span className="text-sm text-gray-500">{feat}</span>
              <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">Coming Soon</span>
            </div>
          ))}

          {/* WhatsApp Share */}
          <button onClick={handleShare}
            className="w-full h-14 bg-[#25D366] text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg"
            data-testid="whatsapp-share-btn"
          >
            <Share2 size={18} /> Share on WhatsApp
          </button>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
